import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { saveMatch, safeConvertToTimestamp, convertToSerializableMatch } from '../services/matchService';
import { useDispatch } from 'react-redux';
import { setCurrentMatch } from '../store/matchSlice';

const ConfigMatchPage: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [matchName, setMatchName] = useState('Match de Football');
  const [matchType, setMatchType] = useState('amical');
  const [matchDate, setMatchDate] = useState(() => {
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const day = String(today.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  });
  const [teamAName, setTeamAName] = useState('Mon Équipe');
  const [teamBName, setTeamBName] = useState('Adversaire');
  const [isHomeMatch, setIsHomeMatch] = useState(true);

  const handleStartMatch = async () => {
    // Validation
    if (!matchName) {
      alert('Veuillez saisir le nom du match');
      return;
    }

    if (!matchType) {
      alert('Veuillez sélectionner le type de match');
      return;
    }

    if (!matchDate) {
      alert('Veuillez sélectionner la date du match');
      return;
    }

    if (!teamAName) {
      alert('Veuillez saisir le nom de votre équipe');
      return;
    }

    if (!teamBName) {
      alert('Veuillez saisir le nom de l\'équipe adverse');
      return;
    }

    try {
      // Créer le match
      const matchData = {
        name: matchName,
        type: matchType,
        date: safeConvertToTimestamp(new Date(matchDate)),
        status: 'upcoming' as const, // Utiliser un statut valide
        scoreA: 0,
        scoreB: 0,
        isHomeMatch: isHomeMatch,
        teamA: {
          name: teamAName,
          players: []
        },
        teamB: {
          name: teamBName,
          players: []
        },
        events: [],
        duration: 90,
        location: isHomeMatch ? 'Domicile' : 'Extérieur',
        time: new Date().toLocaleTimeString()
      };

      // Sauvegarder le match et obtenir son ID
      const matchId = await saveMatch(matchData);

      // Mettre à jour le store Redux
      const newMatch = {
        ...matchData,
        id: matchId || '',
        date: safeConvertToTimestamp(matchDate),
        teamA: {
          name: teamAName,
          players: []
        },
        teamB: {
          name: teamBName,
          players: []
        }
      };

      // Convertir le match en format sérialisable avant de le dispatcher à Redux
      const serializableMatch = convertToSerializableMatch(newMatch);
      dispatch(setCurrentMatch(serializableMatch));

      // Rediriger vers la configuration de l'équipe
      navigate(matchId ? `/config-equipe?matchId=${matchId}` : '/matches');
    } catch (error) {
      console.error('Erreur lors de la création du match :', error);
      alert('Impossible de créer le match. Veuillez réessayer.');
    }
  };

  const matchTypes = [
    { value: 'amical', label: 'Amical' },
    { value: 'championnat', label: 'Championnat' },
    { value: 'coupe', label: 'Coupe' },
    { value: 'tournoi', label: 'Tournoi' }
  ];

  return (
    <div className="bg-white text-gray-900 min-h-screen flex items-center justify-center p-8">
      <div className="w-full max-w-md bg-white rounded-2xl shadow-2xl p-8 border-2 border-blue-500">
        <div className="mb-6">
          <label className="block mb-3 font-bold text-blue-800">Nom du Match</label>
          <input 
            type="text" 
            value={matchName}
            onChange={(e) => setMatchName(e.target.value)}
            className="w-full px-4 py-3 bg-white border-2 border-blue-300 rounded-lg text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 transition"
            placeholder="Nom du match"
          />
        </div>

        <div className="mb-6">
          <label className="block mb-3 font-bold text-blue-800">Type de Match</label>
          <div className="grid grid-cols-2 gap-4">
            {matchTypes.map((type) => (
              <button
                key={type.value}
                className={`
                  py-3 px-4 rounded-lg transition duration-300 transform
                  ${matchType === type.value 
                    ? 'bg-blue-600 text-white scale-105 shadow-lg' 
                    : 'bg-white text-blue-700 hover:bg-blue-50 hover:scale-105 border-2 border-blue-300'}
                `}
                onClick={() => setMatchType(type.value)}
              >
                {type.label}
              </button>
            ))}
          </div>
        </div>

        <div className="mb-6">
          <label className="block mb-3 font-bold text-blue-800">Date du Match</label>
          <input 
            type="date" 
            value={matchDate}
            onChange={(e) => setMatchDate(e.target.value)}
            className="w-full px-4 py-3 bg-white border-2 border-blue-300 rounded-lg text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 transition"
          />
        </div>

        <div className="mb-6">
          <div className="flex items-center justify-between mb-3">
            <label className="font-bold text-blue-800">Match à Domicile</label>
            <div className="flex items-center">
              <span className="mr-3 text-sm font-medium text-blue-700">
                {isHomeMatch ? 'Domicile' : 'Extérieur'}
              </span>
              <label className="flex items-center cursor-pointer">
                <div className="relative">
                  <input 
                    type="checkbox" 
                    className="sr-only" 
                    checked={isHomeMatch}
                    onChange={() => setIsHomeMatch(!isHomeMatch)}
                  />
                  <div className={`
                    w-10 h-4 rounded-full shadow-inner transition-colors duration-300
                    ${isHomeMatch ? 'bg-blue-600' : 'bg-blue-300'}
                  `}></div>
                  <div className={`
                    dot absolute -left-1 -top-1 bg-white w-6 h-6 rounded-full 
                    shadow transition-transform duration-300
                    ${isHomeMatch ? 'translate-x-full' : ''}
                  `}></div>
                </div>
              </label>
            </div>
          </div>
        </div>

        <div className="mb-6">
          <label className="block mb-3 font-bold text-blue-800">Mon équipe</label>
          <input 
            type="text" 
            value={teamAName}
            onChange={(e) => setTeamAName(e.target.value)}
            className="w-full px-4 py-3 bg-white border-2 border-blue-300 rounded-lg text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 transition"
            placeholder="Nom de votre équipe"
          />
        </div>

        <div className="mb-8">
          <label className="block mb-3 font-bold text-blue-800">Equipe adverse</label>
          <input 
            type="text" 
            value={teamBName}
            onChange={(e) => setTeamBName(e.target.value)}
            className="w-full px-4 py-3 bg-white border-2 border-blue-300 rounded-lg text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 transition"
            placeholder="Nom de l'équipe adverse"
          />
        </div>

        <button 
          onClick={handleStartMatch}
          className="w-full bg-blue-600 text-white py-4 rounded-lg hover:bg-blue-700 transition duration-300 transform hover:scale-105 text-lg font-bold"
        >
          Commencer le Match
        </button>
      </div>
    </div>
  );
};

export default ConfigMatchPage;
